import { validSRPName, validSRPVersion, validSwPartNumber } from "./regex.js";
import { checkIfFieldIsRequiredPerArchitecture } from "./architectureTypeDataProcess";
import { isSigningAllowedForModule } from "./utilityFunctions";

// Helper functions to extract validation logic
const validateArrayField = (fieldValue) => {
    return fieldValue.some(value => typeof value === 'string' && value.trim() !== '') ? "" : "This field is required";
};

const validateSingleField = (fieldValue) => {
    if (typeof fieldValue === 'boolean' || typeof fieldValue === 'number') {
        return "";
    }
    
    return fieldValue?.length ? "" : "This field is required";
};

const getValidationError = (fieldValue) => {
    if (Array.isArray(fieldValue)) {
        return validateArrayField(fieldValue);
    }
    
    return validateSingleField(fieldValue);
};

const getFieldError = (value) => {
    return value ? "" : "This field is required";
};

// Collapsing errors/data into a single state would fix these extra long function headers
export const validateFormAndGetIsValid = ({
    textFieldErrors, 
    textFieldValues, setTextFieldErrors, 
    moduleInformation, setModuleInformationErrors, 
    machineList, setMachineListErrors,
    architectureType,
    vehicleProgramOptions,
    allowSigningModules,
}) => {
    console.log("Current architecture type from validateFormAndGetIsValid: ", architectureType);
    const textFieldsValidationResult = validateSRPInformation({textFieldErrors, textFieldValues, setTextFieldErrors, architectureType, moduleName: moduleInformation.moduleName, vehicleProgramOptions});
    const moduleInformationValidationResult = validateModuleInformation({moduleInformation, setModuleInformationErrors, architectureType});
    const machineListValidationResult = validateMachineList({machineList, setMachineListErrors, architectureType, allowSigningModules, moduleName: moduleInformation.moduleName});
    const preassemblyImagesValidationResult = true;
    const previewValidationResult = true; // This is a placeholder, as the preview validation is not implemented yet
    let isValid = true;
    const validationResults = [
        textFieldsValidationResult,
        moduleInformationValidationResult,
        machineListValidationResult,
        preassemblyImagesValidationResult,
        previewValidationResult,
    ];

     isValid = validationResults.every((result) => result === true);

    return {
        isValid,
        validationResults
    }
};

export const validateSRPInformation = ({textFieldErrors, textFieldValues, setTextFieldErrors, architectureType, moduleName, vehicleProgramOptions}) => {

    const srpInformationFields = [
        "srpdName",
        "srpdVersion",
        "srpAssignee",
        "releaseObjectKey",
        "fixVersion",
    ];
    let isValid = true;
    const srpInformationErrorFields = [
    ];

    for (const field of srpInformationFields) {
        let currIsValid = true;
        if (checkIfFieldIsRequiredPerArchitecture(field, architectureType)) {
            currIsValid &&= validateSRPInformationValue(field, textFieldValues[field], setTextFieldErrors);
        }

        if (!currIsValid) {
            srpInformationErrorFields.push(field);
        }

        isValid &&= currIsValid;
    }

    // Validate SRP Name format
    isValid &&= validateSRPNameValue({value: textFieldValues.srpdName, moduleName, vehicleProgramOptions});

    console.log("SRP Information error fields: ", srpInformationErrorFields);
    return isValid;


}
 
export const validateSRPNameValue = ({value, moduleName, vehicleProgramOptions}) => {
    let isValid = validSRPName.test(value);

    // if string contain vehicle program, it should be invalid
    vehicleProgramOptions.forEach((option) => {
        const [_, programCode] = option.value.split("-");
        if (programCode && value.includes(programCode)) {
            isValid = false;
        }
            
    });

    const lowerCaseValue = value.toLowerCase();
    const lowerCaseModuleName = moduleName.toLowerCase();

    if (!lowerCaseValue.includes(lowerCaseModuleName)) {
        isValid = false;
    }

    if (value.split("_").length > 3) {
        isValid = false;
    }

    console.log(`Validated SRP Name: ${value}, Module Name: ${moduleName}, Is Valid: ${isValid}`);
    return isValid;
};


export const validateKeyParticipants = (value, setTextFieldErrors) => {
    validateTextField("srpWatchers", value, setTextFieldErrors);
    if (value.length === 0) {
        return false;
    } else {
        return true;
    }
};


export const validateTextField = (id, value, setTextFieldErrors) => {
    setTextFieldErrors((prevErrors) => ({
        ...prevErrors,
        [id]: getFieldError(value),
    }));
};

export const validateSRPInformationValue = (fieldName, fieldValue, setTextFieldErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    let validationError = getValidationError(fieldValue);

    if (fieldName === "srpdVersion") {
        validationError = validSRPVersion.test(fieldValue) ? "" : "Invalid SRP Version format. Must follow Semantic Versioning";
    }
    setTextFieldErrors((prevErrors) => ({
        ...prevErrors,
        [validationFieldName]: validationError,
    }));
    return validationError === "";
};


// required attributes errors handling
export const validateModuleInformation = ({moduleInformation, setModuleInformationErrors, architectureType}) => {
    let isValid = true;
    console.log("Current architecture type: ", architectureType);
    const moduleInformationFields = [
        "moduleName",
        "vehiclePrograms",
        "lveas",
        "gpdsMilestone",
        "publicRoadWorthy",
        "criticalAttributesImpacted",
    ];
    
    for (const field of moduleInformationFields) {
        let prefix = 'moduleInformation.';
        let pathToField = `${prefix}${field}`;
        if (checkIfFieldIsRequiredPerArchitecture(pathToField, architectureType)) {
            let currIsValid = validateModuleInformationValue(field, moduleInformation[field], setModuleInformationErrors);
            isValid &&= currIsValid;
        }
    }


    return isValid;
};


export const validateModuleInformationValue = (fieldName, fieldValue, setModuleInformationErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    const validationError = getValidationError(fieldValue);

    setModuleInformationErrors((prevErrors) => ({
        ...prevErrors,
        [validationFieldName]: validationError,
    }));

    return validationError === "";
};

export const validateUDSAuthorizationMethod = (udsMethod, i, setMachineListErrors) => {
    let isValid = true;
    let errorMessage = "";

    if (udsMethod.length === 0) {
        isValid = false;
        errorMessage = "This field is required";
    }

    else if (udsMethod.includes("N/A") && udsMethod.length > 1) {
        isValid = false;
        errorMessage = "If 'N/A' is selected, other fields are not allowed.";
    }



    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        newErrors[i].udsAuthorizationMethodValidationError = errorMessage;
        return newErrors;
    });

    return isValid
}


// Helper function to validate machine fields
const validateMachineFields = (machine, machineIndex, architectureType, setMachineListErrors) => {
    const machineListFields = [
        "machineName",
        "machineType",
        "canDiagRxId",
        "canDiagTxId",
    ];
    
    let isValid = true;
    const failedFields = [];
    
    for (const field of machineListFields) {
        const pathToField = `machineList.0.${field}`;
        if (checkIfFieldIsRequiredPerArchitecture(pathToField, architectureType)) {
            const currIsValid = validateMachineValue(machineIndex, field, machine[field], setMachineListErrors);
            isValid = isValid && currIsValid;
            if (!currIsValid) {
                failedFields.push(field);
            }
        }
    }
    
    return { isValid, failedFields };
};

// Helper function to validate variant fields
const validateVariantFields = (variant, machineIndex, variantIndex, architectureType, setMachineListErrors) => {
    const variantListFields = [
        "variantName",
        "logicalDiagAddress",
        "ucmSubordinateId",
        "flashingAdapterAddress",
        "arxmlRequired",
        "vspRequired",
        "otaCapable",
        "flashCapability",
        "commsProtocol",
        "publicTopology",
        "privateTopology",
        "ethernetTopology",
        "asilRating",
        "supplier"
    ];
    
    let isValid = true;
    const failedFields = [];
    
    for (const field of variantListFields) {
        const pathToField = `machineList.0.variantList.0.${field}`;
        if (checkIfFieldIsRequiredPerArchitecture(pathToField, architectureType)) {
            const currIsValid = validateVariantValue(machineIndex, variantIndex, field, variant[field], setMachineListErrors);
            isValid = isValid && currIsValid;
            if (!currIsValid) {
                failedFields.push(field);
            }
        }
    }
    
    return { isValid, failedFields };
};

// Helper function to validate selection criteria for a cluster
const validateSelectionCriteriaList = (cluster, machineIndex, variantIndex, clusterIndex, architectureType, setMachineListErrors) => {
    const selectionCriteriaFields = [
        "programCode",
        "modelYear", 
        "ncPartNumber",
        "mfalCode",
        "did"
    ];
    
    let isValid = true;
    const failedFields = [];
    
    for (let l = 0; l < cluster.selectionCriteriaList.length; l++) {
        const criteria = cluster.selectionCriteriaList[l];
        const pathPrefix = 'machineList.0.variantList.0.clusterList.0.selectionCriteriaList.0.';
        
        for (const field of selectionCriteriaFields) {
            const pathToField = `${pathPrefix}${field}`;
            if (checkIfFieldIsRequiredPerArchitecture(pathToField, architectureType)) {
                const currIsValid = validateSelectionCriteriaValue(machineIndex, variantIndex, clusterIndex, l, field, criteria[field], setMachineListErrors);
                isValid = isValid && currIsValid;
                if (!currIsValid) {
                    failedFields.push(field);
                }
            }
        }
    }
    
    return { isValid, failedFields };
};

// Helper function to check if field should be validated
const shouldValidateClusterField = (field, cluster, pathPrefix, architectureType) => {
    return checkIfFieldIsRequiredPerArchitecture(`${pathPrefix}${field}`, architectureType);
};

// Helper function to validate cluster fields
const validateClusterFields = (cluster, machineIndex, variantIndex, clusterIndex, architectureType, setMachineListErrors) => {
    const clusterListFields = [
        "clusterName", "changeLevel", "clusterVersion", "clusterUrl", "clusterType",
        "configurationType", "ecuExtractVersion", "f163D2SpecVersion", "part2SpecUrl",
        "wersConcernNum", "f113ECUAssemblyNum", "f111CoreAssemblyNum", "swPartNumber",
        "actionType", "updateType", "udsMethod", "inhibitRequired", "sessionSequence",
        "swDownloadMethod", "updateTimeInSeconds", "assemblyCritical", "productionReleaseIntent",
        "swSignRequest", "swSigningId", "ucmPackagePath", "programmingMethod"
    ];
    
    const pathPrefix = 'machineList.0.variantList.0.clusterList.0.';
    let isValid = true;
    const failedFields = [];
    
    for (const field of clusterListFields) {
        if (shouldValidateClusterField(field, cluster, pathPrefix, architectureType)) {
            const currIsValid = validateClusterValue(machineIndex, variantIndex, clusterIndex, field, cluster[field], setMachineListErrors);
            isValid = isValid && currIsValid;
            if (!currIsValid) {
                failedFields.push(field);
            }
        }
    }
    
    return { isValid, failedFields };
};

// Helper function to validate conditional cluster fields
const validateConditionalClusterFields = ({cluster, machineIndex, variantIndex, clusterIndex, setMachineListErrors, allowSigningModules, moduleName}) => {
    let isValid = true;
    const failedFields = [];
    
    // Validate configuration type for CONFIGURATION clusters
    if (cluster.clusterType === "CONFIGURATION") {
        const currIsValid = validateClusterValue(machineIndex, variantIndex, clusterIndex, "configurationType", cluster.configurationType, setMachineListErrors);
        isValid = isValid && currIsValid;
        if (!currIsValid) {
            failedFields.push("configurationType");
        }
    }

    if (cluster.swSignRequest === "PROD_SIGN" || cluster.swSignRequest === "DEV_SIGN") {
        if (isSigningAllowedForModule(allowSigningModules, moduleName)) {
            const currIsValid = validateClusterValue(machineIndex, variantIndex, clusterIndex, "vbfType", cluster.vbfType, setMachineListErrors);
            isValid = isValid && currIsValid;
            if (!currIsValid) {
                failedFields.push("vbfType");
            }
        } else {
            updateClusterErrors(machineIndex, variantIndex, clusterIndex, "swSignRequestValidationError", "Signing is currently not allowed for this module", setMachineListErrors);
            isValid = false;
            failedFields.push("swSignRequest");
        }

        // Validate signing ID for signed requests, this is default behavior no matter if isSigningAllowedForModule or not, as this rule is sync with schema validation
        const currIsValid = validateClusterValue(machineIndex, variantIndex, clusterIndex, "swSigningId", cluster.swSigningId, setMachineListErrors);
        isValid = isValid && currIsValid;
        if (!currIsValid) {
            failedFields.push("swSigningId");
        }

    } else if (cluster.swSignRequest !== "NO_SIGN") {
        updateClusterErrors(machineIndex, variantIndex, clusterIndex, "swSignRequestValidationError", "Invalid signing request type", setMachineListErrors);
        isValid = false;
        failedFields.push("swSignRequest");
    }
    
    return { isValid, failedFields };
};

// Helper function to validate a single cluster
const validateSingleCluster = ({cluster, machineIndex, variantIndex, clusterIndex, architectureType, setMachineListErrors, allowSigningModules, moduleName}) => {
    // Validate standard cluster fields
    const fieldsResult = validateClusterFields(cluster, machineIndex, variantIndex, clusterIndex, architectureType, setMachineListErrors);
    
    // Validate conditional fields
    const conditionalResult = validateConditionalClusterFields({cluster, machineIndex, variantIndex, clusterIndex, setMachineListErrors, allowSigningModules, moduleName});
    
    // Validate selection criteria
    const selectionResult = validateSelectionCriteriaList(cluster, machineIndex, variantIndex, clusterIndex, architectureType, setMachineListErrors);
    
    const isValid = fieldsResult.isValid && conditionalResult.isValid && selectionResult.isValid;
    const failedFields = [
        ...fieldsResult.failedFields,
        ...conditionalResult.failedFields,
        ...selectionResult.failedFields
    ];
    
    return { isValid, failedFields };
};

// Helper function to validate clusters within a variant
const validateClusterList = ({variant, machineIndex, variantIndex, architectureType, setMachineListErrors, allowSigningModules, moduleName}) => {
    let isValid = true;
    const failedFields = [];
    
    for (let k = 0; k < variant.clusterList.length; k++) {
        const cluster = variant.clusterList[k];
        const result = validateSingleCluster({cluster, machineIndex, variantIndex, clusterIndex: k, architectureType, setMachineListErrors, allowSigningModules, moduleName});
        
        isValid = isValid && result.isValid;
        failedFields.push(...result.failedFields);
    }
    
    return { isValid, failedFields };
};

// Helper function to validate variants within a machine
const validateVariantList = ({machine, machineIndex, architectureType, setMachineListErrors, allowSigningModules, moduleName}) => {
    let isValid = true;
    const failedFields = [];
    
    for (let j = 0; j < machine.variantList.length; j++) {
        const variant = machine.variantList[j];
        
        // Validate variant fields
        const variantResult = validateVariantFields(variant, machineIndex, j, architectureType, setMachineListErrors);
        
        isValid = isValid && variantResult.isValid;
        failedFields.push(...variantResult.failedFields);
        
        // Validate clusters within this variant
        const clusterResult = validateClusterList({variant, machineIndex, variantIndex: j, architectureType, setMachineListErrors, allowSigningModules, moduleName});
        isValid = isValid && clusterResult.isValid;
        failedFields.push(...clusterResult.failedFields);
    }
    
    return { isValid, failedFields };
};

// Helper function to validate a single machine
const validateSingleMachine = ({machine, machineIndex, architectureType, setMachineListErrors, allowSigningModules, moduleName}) => {
    let isValid = true;
    const failedFields = [];
    
    // Validate machine fields
    const machineResult = validateMachineFields(machine, machineIndex, architectureType, setMachineListErrors);
    
    isValid = isValid && machineResult.isValid;
    failedFields.push(...machineResult.failedFields);
    
    // Validate variants within this machine
    const variantResult = validateVariantList({machine, machineIndex, architectureType, setMachineListErrors, allowSigningModules, moduleName});
    isValid = isValid && variantResult.isValid;
    failedFields.push(...variantResult.failedFields);
    
    return { isValid, failedFields };
};

// machineList errors handling
export const validateMachineList = ({machineList, setMachineListErrors, architectureType, allowSigningModules, moduleName}) => {
    let isValid = true;
    const failedValidationFields = [];
    
    for (let i = 0; i < machineList.length; i++) {
        const machine = machineList[i];
        const result = validateSingleMachine({machine, machineIndex: i, architectureType, setMachineListErrors, allowSigningModules, moduleName});
        
        isValid = isValid && result.isValid;
        failedValidationFields.push(...result.failedFields);
    }

    console.log("Failed validation fields: ", failedValidationFields);

    return isValid;
};

export const validateMachineValue = (machineIndex, fieldName, fieldValue, setMachineListErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    const validationError = getValidationError(fieldValue);

    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        newErrors[machineIndex][validationFieldName] = validationError;
        return newErrors;
    });

    return validationError === "";
}

export const validateVariantValue = (machineIndex, variantIndex, fieldName, fieldValue, setMachineListErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    const validationError = getValidationError(fieldValue);
    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        newErrors[machineIndex].variantListErrors[variantIndex][validationFieldName] = validationError;
        return newErrors;
    });

    return validationError === "";
}


// Helper function to get field-specific validation error
const getFieldSpecificValidationError = (fieldName, fieldValue) => {

    if (fieldName === "clusterUrl") {
        if (fieldValue.length > 980) {
            return "Container URL exceeds maximum length of 980 characters.";
        }
    }

    if (fieldName === "swPartNumber") {
        if (!validSwPartNumber.test(fieldValue)) {
            return "Invalid SW Part Number format.";
        }
    }

    if (fieldName === "sessionSequence") {
        const sessionSequencePattern = /^\d+\/\d+$/;
        if (!sessionSequencePattern.test(fieldValue)) {
            return "Invalid format. Input must match format 'number/number'.";
        }
    }
    
    if (fieldName === "updateTimeInSeconds") {
        console.log("Validating updateTimeInSeconds", fieldValue)
        if (!Number.isInteger(fieldValue)) {
            return "Invalid format. Input must be an integer.";
        }
    }
    
    return "";
};

// Helper function to update cluster errors
const updateClusterErrors = (machineIndex, variantIndex, clusterIndex, validationFieldName, validationError, setMachineListErrors) => {
    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        newErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex][validationFieldName] = validationError;
        return newErrors;
    });
};

// Helper function to remove cluster error if valid
const removeClusterErrorIfValid = (machineIndex, variantIndex, clusterIndex, validationFieldName, setMachineListErrors) => {
    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        const clusterErrors = newErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex];
        if (clusterErrors.hasOwnProperty(validationFieldName)) {
            delete clusterErrors[validationFieldName];
        }
        return newErrors;
    });
};

export const validateClusterValue = (machineIndex, variantIndex, clusterIndex, fieldName, fieldValue, setMachineListErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    
    // Get field-specific validation error first
    let validationError = getFieldSpecificValidationError(fieldName, fieldValue);
    
    // If no field-specific error, check generic validation
    if (!validationError) {
        validationError = getValidationError(fieldValue);
    }
    
    // Update errors
    updateClusterErrors(machineIndex, variantIndex, clusterIndex, validationFieldName, validationError, setMachineListErrors);
    
    // Remove error if valid
    const isValid = validationError === "";
    if (isValid) {
        removeClusterErrorIfValid(machineIndex, variantIndex, clusterIndex, validationFieldName, setMachineListErrors);
    }
    
    return isValid;
};


export const validateSelectionCriteriaValue = (machineIndex, variantIndex, clusterIndex, criteriaIndex, fieldName, fieldValue, setMachineListErrors) => {
    const validationFieldName = `${fieldName}ValidationError`;
    const validationError = getValidationError(fieldValue);

    setMachineListErrors((prevErrors) => {
        const newErrors = structuredClone(prevErrors);
        newErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].selectionCriteriaListErrors[criteriaIndex][validationFieldName] = validationError;
        return newErrors;
    }
    );
    let isValid = validationError === "";
    if (isValid) {
        // remove the current key from the errors object
        setMachineListErrors((prevErrors) => {
            const newErrors = structuredClone(prevErrors);
            if (newErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].selectionCriteriaListErrors[criteriaIndex].hasOwnProperty(validationFieldName)) {
                delete newErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].selectionCriteriaListErrors[criteriaIndex][validationFieldName];
            }
            return newErrors;
        });
    }
    return validationError === "";
};


const getUrlValidationError = (isUrlValid) => {
    return isUrlValid ? "" : "Invalid URL. Must contain 'CNR_' and end with '.tar.gz' or '.tgz'";
};

export const validateArtifactUrl = (url, machineIndex, clusterIndex, setMachineListErrors) => {
    const isUrlValid = url?.includes("CNR_") && (url?.endsWith(".tar.gz") || url?.endsWith(".tgz"));

    setMachineListErrors((prevMachineListErrors) => {
        const updatedMachineListErrors = structuredClone(prevMachineListErrors);
        updatedMachineListErrors[machineIndex].clusterListErrors[clusterIndex].clusterUrlValidationError = getUrlValidationError(isUrlValid);
        return updatedMachineListErrors;
    });

    return isUrlValid;
};

// preassembly images errors handling
export const validatePreassemblyImage = (preassemblyImage, setPreassemblyImageErrors, architectureType) => {
        return true;
};


const getPreassemblyImageValidationError = (fieldValue) => {
    return (!fieldValue && fieldValue !== false) ? "This field is required" : "";
};

export const validatePreassemblyImageTextField = (fieldName, fieldValue, setPreassemblyImageErrors) => {
    const validationError = getPreassemblyImageValidationError(fieldValue);
    const validationFieldName = `${fieldName}ValidationError`;

    setPreassemblyImageErrors((prevErrors) =>
        ({ ...prevErrors, [validationFieldName]: validationError })
    );

    return validationError === "";
};



// Helper function to check if an object has validation errors
const hasValidationErrors = (obj) => {
    for (const key in obj) {
        if (key.endsWith("ValidationError") && obj[key]) {
            return true;
        }
    }
    return false;
};

// Helper function to validate selection criteria errors
const validateSelectionCriteriaErrors = (selectionCriteriaList) => {
    for (const selectionCriteria of selectionCriteriaList) {
        if (hasValidationErrors(selectionCriteria)) {
            return false;
        }
    }
    return true;
};

// Helper function to validate cluster errors
const validateClusterErrors = (clusterErrors) => {
    if (hasValidationErrors(clusterErrors)) {
        return false;
    }
    
    if (clusterErrors.selectionCriteriaListErrors) {
        return validateSelectionCriteriaErrors(clusterErrors.selectionCriteriaListErrors);
    }
    
    return true;
};

// Helper function to validate variant errors
const validateVariantErrors = (variantErrors) => {
    if (hasValidationErrors(variantErrors)) {
        return false;
    }
    
    if (variantErrors.clusterListErrors) {
        for (const clusterError of variantErrors.clusterListErrors) {
            if (!validateClusterErrors(clusterError)) {
                return false;
            }
        }
    }
    
    return true;
};

// Helper function to validate machine errors
const validateMachineErrors = (machineErrors) => {
    if (hasValidationErrors(machineErrors)) {
        return false;
    }
    
    if (machineErrors.variantListErrors) {
        for (const variantError of machineErrors.variantListErrors) {
            if (!validateVariantErrors(variantError)) {
                return false;
            }
        }
    }
    
    return true;
};

export const softValidateMachineList = (machineListErrors) => {
    try {
        for (const machineError of machineListErrors) {
            if (!validateMachineErrors(machineError)) {
                return false;
            }
        }
        return true;
    } catch (e) {
        console.error("Error in softValidateMachineList: ", e);
        return false;
    }
}

export const softValidateModuleInformation = (moduleInformationErrors) => {
  // loop through all keys in the moduleInformation object
  // if any key that ends with ValidationError postfix is defined, return true
  
  try {
        for (const key in moduleInformationErrors) {
            if (key.endsWith("ValidationError") && moduleInformationErrors[key]) {
            return false;
            }
        }
        return true;
    } catch (e) {
        console.error("Error in softValidateModuleInformation: ", e);
        return false;
    }
}


export const softValidateTextFieldValues = (textFieldErrors) => {
  // loop through all keys in the textFieldValues object
  // if any key that ends with ValidationError postfix is defined, return true
  try {  
        for (const key in textFieldErrors) {
            if (key.endsWith("ValidationError") && textFieldErrors[key]) {
            return false;
            }
        }
        return true;  
    } catch (e) {
        console.error("Error in softValidateTextFieldValues: ", e);
        return false;
    }
}

export const isSwSigningIdRequired = (swSignRequestValue, vbfTypeValue) => {
    return (swSignRequestValue === "DEV_SIGN" || swSignRequestValue === "PROD_SIGN") && vbfTypeValue === "COMPLEX";
}

export const clearSigningIdValidationError = (machineIndex, variantIndex, clusterIndex, setMachineListErrors) => {
        setMachineListErrors(prevState => {
            const newState = [...prevState];
            const errorPath = newState[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex];
            if (errorPath?.swSigningIdValidationError) {
                delete errorPath.swSigningIdValidationError;
            }
            return newState;
        });
    };